import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  Input,
  Select,
  Option,
  Modal,
  ModalClose,
  Typography,
  Sheet,
  Box,
  Textarea,
} from "@mui/joy";
import toast from "react-hot-toast";
import axiosInstance from "../../api/axios";
import AuthContext from "../../contexts/authContext";

export default function AddCustomer({
  openCustomer,
  setOpenCustomer,
  editId,
  setEditId,
}) {
  const { userData, setRefresh } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
    scheme: "",
    amount: "",
    agent: "",
    area: "",
    joinedDate: new Date().toISOString().split("T")[0],
    idType: "",
    idNumber: "",
    lat: "",
    long: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For Latitude & Longitude — allow only float numbers
    if (name === "lat" || name === "long") {
      // allow empty
      if (value === "") {
        setForm((p) => ({ ...p, [name]: "" }));
        return;
      }

      // allow only valid float pattern
      const floatRegex = /^-?\d*\.?\d*$/;
      if (!floatRegex.test(value)) return; // block invalid input
    }

    // update normally for all fields
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits.");
      return;
    }

    const {
      name,
      mobile,
      email,
      address,
      area,
      joinedDate,
      idType,
      idNumber,
      agent,
      lat,
      long,
    } = form;
    // Validate LATITUDE
    if (lat !== "" && isNaN(Number(lat))) {
      toast.error("Latitude must be a valid number.");
      return;
    }

    // Validate LONGITUDE
    if (long !== "" && isNaN(Number(long))) {
      toast.error("Longitude must be a valid number.");
      return;
    }
    if (
      !name ||
      !mobile ||
      !email ||
      !address ||
      !area ||
      !joinedDate ||
      !idType ||
      !idNumber ||
      !agent
    ) {
      toast.error("All fields are required.");
      return;
    }
    try {
      const res = await axiosInstance.post("/customers/add", {
        name,
        mobile,
        email,
        address,
        area,
        joinedDate,
        idType,
        idNumber,
        agent,
        lat,
        long,
      });

      if (res.status === 200) {
        toast.success("Customer added successfully!");
        setForm({
          name: "",
          mobile: "",
          email: "",
          address: "",
          area: "",
          joinedDate: "",
          idType: "",
          idNumber: "",
          agent: "",
          lat: "",
          long: "",
        });
        setOpenCustomer(false);
        setRefresh(true);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Customer already exists");
        return;
      }
      toast.error("An error occurred while adding the customer.");
    }
  };
  const handleEditCustomer = async (e) => {
    e.preventDefault();
    if (form.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits.");
      return;
    }

    // Validate LATITUDE
    if (form.lat !== "" && isNaN(Number(form.lat))) {
      toast.error("Latitude must be a valid number.");
      return;
    }

    // Validate LONGITUDE
    if (form.long !== "" && isNaN(Number(form.long))) {
      toast.error("Longitude must be a valid number.");
      return;
    }
    try {
      const res = await axiosInstance.put(`/customers/edit/${editId}`, {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        address: form.address,
        area: form.area,
        joinedDate: form.joinedDate,
        idType: form.idType,
        idNumber: form.idNumber,
        agent: form.agent,
        lat: form.lat,
        long: form.long,
      });

      if (res.status === 200) {
        toast.success("Customer updated successfully!");
        setOpenCustomer(false);
        setEditId("");
        setRefresh(true);
      }
    } catch (err) {
      toast.error("An error occurred while updating the user.");
    }
  };
  const handleCloseCustomerModal = () => {
    setForm({
      name: "",
      mobile: "",
      email: "",
      address: "",
      area: "",
      joinedDate: new Date().toISOString().split("T")[0],
      idType: "",
      idNumber: "",
      agent: "",
      lat: "",
      long: "",
    });
    setOpenCustomer(false);
    setRefresh(true);
    setEditMode(false);
    setEditId("");
  };
  useEffect(() => {
    if (editId) {
      setEditMode(true);
      const editCustomer = userData?.customers?.find(
        (ite) => ite._id === editId
      );
      if (editCustomer) {
        setForm({
          name: editCustomer.name || "",
          mobile: editCustomer.mobile || "",
          email: editCustomer.email || "",
          address: editCustomer.address || "",
          area: editCustomer.area._id || "",
          joinedDate: editCustomer.joinedDate || "",
          idType: editCustomer.idproofType || "",
          idNumber: editCustomer.idproofNumber || "",
          agent: editCustomer.agent?._id || "",
          lat: editCustomer.lat || "",
          long: editCustomer.long || "",
        });
      }
    } else {
      setEditMode(false);
      setForm({
        name: "",
        mobile: "",
        email: "",
        address: "",
        area: "",
        joinedDate: new Date().toISOString().split("T")[0],
        idType: "",
        idNumber: "",
        agent: "",
        lat: "",
        long: "",
      });
    }
  }, [editId, userData]);

  return (
    <Modal
      open={openCustomer}
      onClose={handleCloseCustomerModal}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 500,
          width: "100%",
          bgcolor: "#FEDDDD",
          p: 4,
          borderRadius: "md",
          boxShadow: "lg",
          maxHeight: "90vh",
          overflowY: "auto",
          m: 2,
        }}
      >
        <ModalClose
          sx={{
            m: 1,
            "& svg": { color: "#800001" },
            "&:hover": {
              backgroundColor: "#800001",
              "& svg": { color: "#fff" },
            },
          }}
          onClick={handleCloseCustomerModal}
        />

        <Typography
          level="h4"
          sx={{
            color: "#800001",
            fontWeight: "bold",
            mb: 2,
            textAlign: "center",
          }}
        >
          {editMode ? "Edit Customer" : "Add Customer"}
        </Typography>

        <form
          onSubmit={editMode ? handleEditCustomer : handleSubmit}
          style={{ overflowY: "auto" }}
        >
          <FormControl sx={{ mb: 2 }}>
            {/* <FormLabel>Name</FormLabel> */}
            <Input
              placeholder="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </FormControl>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 2 },
            }}
          >
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Mobile</FormLabel> */}
              <Input
                placeholder="Mobile"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                required
              />
            </FormControl>

            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Email</FormLabel> */}
              <Input
                placeholder="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </FormControl>
          </Box>

          <FormControl sx={{ mb: 2 }}>
            {/* <FormLabel>Address</FormLabel> */}
            <Textarea
              placeholder="Address"
              minRows={2}
              name="address"
              value={form.address}
              onChange={handleChange}
              required
            />
          </FormControl>
          <FormControl sx={{ mb: 2, flex: 1 }}>
            {/* <FormLabel>Zone</FormLabel> */}
            <Select
              placeholder="Agent"
              name="agent"
              value={form.agent}
              onChange={(e, val) =>
                setForm((prev) => ({ ...prev, agent: val }))
              }
              required
            >
              {userData.users
                ?.filter((user) => user.role === "agent")
                ?.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} - {user.mobile}
                  </Option>
                ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 2 },
            }}
          >
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Zone</FormLabel> */}
              <Select
                placeholder="Area"
                name="area"
                value={form.area}
                onChange={(e, val) =>
                  setForm((prev) => ({ ...prev, area: val }))
                }
                required
              >
                {userData.areas
                  ?.filter((a) => a.status !== "erased")
                  .map((area) => (
                    <Option key={area._id} value={area._id}>
                      {area.areaName}
                    </Option>
                  ))}
              </Select>
            </FormControl>
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Joining Date</FormLabel> */}
              <Input
                placeholder="Joining Date"
                name="joinedDate"
                type="date"
                value={form.joinedDate}
                onChange={handleChange}
                required
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 2 },
            }}
          >
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>ID Type</FormLabel> */}
              <Select
                placeholder="ID Type"
                name="idType"
                value={form.idType}
                onChange={(e, val) =>
                  setForm((prev) => ({ ...prev, idType: val }))
                }
                required
              >
                <Option value="aadhaar">Aadhaar</Option>
                <Option value="pan">PAN</Option>
                <Option value="voter">Voter ID</Option>
                <Option value="driving">Driving License</Option>
              </Select>
            </FormControl>

            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>ID Number</FormLabel> */}
              <Input
                placeholder="ID Number"
                name="idNumber"
                value={form.idNumber}
                onChange={handleChange}
                required
              />
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 2 },
            }}
          >
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Mobile</FormLabel> */}

              <Input
                placeholder="Latitude"
                name="lat"
                value={form.lat}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl sx={{ mb: 2, flex: 1 }}>
              {/* <FormLabel>Mobile</FormLabel> */}
              <Input
                placeholder="Longitude"
                name="long"
                value={form.long}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="solid"
            sx={{
              mt: 3,
              backgroundColor: "#800001",
              color: "white",
              "&:hover": {
                backgroundColor: "#800001",
                color: "#fff",
              },
            }}
          >
            Submit
          </Button>
        </form>
      </Sheet>
    </Modal>
  );
}
